import React, { useState } from 'react';
import { submitContactForm } from '../services/api';
import { Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import SEO from '../components/SEO';

const contactSchema = z.object({
  name: z.string().min(2, 'İsim en az 2 karakter olmalıdır'),
  email: z.string().email('Geçerli bir e-posta adresi giriniz'),
  phone: z.string().optional(),
  subject: z.string().min(3, 'Konu en az 3 karakter olmalıdır'),
  message: z.string().min(10, 'Mesaj en az 10 karakter olmalıdır'),
});

export default function ContactPage() {
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      await submitContactForm(data);
      toast.success('Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.');
      reset();
    } catch (error) {
      toast.error('Mesaj gönderilirken bir hata oluştu. Lütfen tekrar deneyiniz.');
      console.error('Error submitting contact form:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <SEO 
        title="İletişim"
        description="İstanbul Turda ile iletişime geçin. VIATERRA Travel Agency - Esenyurt, İstanbul. Telefon: +90 541 411 23 73"
      />
      
      {/* Header */}
      <section className="bg-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="contact-page-title">
            İletişim
          </h1>
          <p className="text-lg text-gray-300">
            Sorularınız için bize ulaşın, size yardımcı olmaktan mutluluk duyarız
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">İletişim Bilgileri</h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="text-orange-500" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Telefon</h3>
                      <a
                        href="tel:+905414112373"
                        className="text-gray-600 hover:text-orange-500 transition-colors"
                        data-testid="contact-phone"
                      >
                        +90 541 411 23 73
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="text-orange-500" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">E-posta</h3>
                      <a
                        href="mailto:info@istanbulturda.com"
                        className="text-gray-600 hover:text-orange-500 transition-colors"
                        data-testid="contact-email"
                      >
                        info@istanbulturda.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-orange-500" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Adres</h3>
                      <p className="text-gray-600">
                        VIATERRA TRAVEL AGENCY<br />
                        Esenkent Mahallesi Onur Güvener Cd<br />
                        Necati Cumali Sk. Truva Evleri No: 8<br />
                        Esenyurt / İstanbul<br />
                        <span className="text-orange-500 font-semibold mt-1 inline-block">TÜRSAB: 18741</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-3">Çalışma Saatleri</h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>Hafta içi (Pzt-Cum)</span>
                    <span className="font-ui font-bold">09:00 - 18:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Hafta sonu</span>
                    <span className="font-ui font-bold text-red-500">Kapalı</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-8 md:p-10 border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Bize Mesaj Gönderin</h2>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">İsim *</Label>
                      <Input
                        id="name"
                        {...register('name')}
                        placeholder="Adınız Soyadınız"
                        data-testid="contact-name"
                      />
                      {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name.message}</p>}
                    </div>

                    <div>
                      <Label htmlFor="email">E-posta *</Label>
                      <Input
                        id="email"
                        type="email"
                        {...register('email')}
                        placeholder="ornek@email.com"
                        data-testid="contact-email-input"
                      />
                      {errors.email && <p className="text-sm text-red-500 mt-1">{errors.email.message}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone">Telefon</Label>
                      <Input
                        id="phone"
                        {...register('phone')}
                        placeholder="+90 5XX XXX XX XX"
                        data-testid="contact-phone-input"
                      />
                      {errors.phone && <p className="text-sm text-red-500 mt-1">{errors.phone.message}</p>}
                    </div>

                    <div>
                      <Label htmlFor="subject">Konu *</Label>
                      <Input
                        id="subject"
                        {...register('subject')}
                        placeholder="Mesaj konusu"
                        data-testid="contact-subject"
                      />
                      {errors.subject && <p className="text-sm text-red-500 mt-1">{errors.subject.message}</p>}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">Mesaj *</Label>
                    <textarea
                      id="message"
                      {...register('message')}
                      placeholder="Mesajınızı buraya yazın..."
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      data-testid="contact-message"
                    />
                    {errors.message && <p className="text-sm text-red-500 mt-1">{errors.message.message}</p>}
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full md:w-auto bg-orange-500 hover:bg-orange-600 text-white px-12 py-6 rounded-xl font-ui font-bold text-lg"
                    data-testid="contact-submit"
                  >
                    {submitting ? 'Gönderiliyor...' : 'Mesaj Gönder'}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
